<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("reports.loading") }}</div>
    <template v-else>
      <!-- G1: a failed /reports/dashboard used to replace the ENTIRE page with
           an error line — taking the generate button with it, so one failed
           summary call looked like "reports don't work at all". The dashboard
           only feeds the preview and the charts; generating a report does not
           need it. Degrade, with a retry, instead of blanking the screen. -->
      <div v-if="error" class="note" data-test="dashboard-error">
        {{ $t("reports.summaryUnavailable") }}
        <button class="btn ghost sm" type="button" data-test="dashboard-retry" @click="loadDashboard">
          {{ $t("reports.retry") }}
        </button>
      </div>
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("reports.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("reports.title") }}</div>
        </div>
        <div class="searchbar grow" style="max-width: 260px">
          🔎 <input class="input" type="search" v-model="q" :placeholder="$t('reports.searchPh')" />
        </div>
      </div>

      <div class="grid2">
        <!-- report configuration -->
        <div class="panel">
          <div class="panel__head"><h4>🧮 {{ $t("reports.config.title") }}</h4></div>
          <div class="panel__body">
            <div class="form one">
              <div class="field">
                <label>{{ $t("reports.config.type") }} <span class="req">*</span></label>
                <select class="select" v-model="reportType">
                  <option v-for="t in reportTypes" :key="t" :value="t">{{ $t("reports.types." + t) }}</option>
                </select>
              </div>
              <div class="form">
                <div class="field">
                  <label>{{ $t("reports.config.from") }}</label>
                  <input class="input" type="date" v-model="fromDate" />
                </div>
                <div class="field">
                  <label>{{ $t("reports.config.to") }}</label>
                  <input class="input" type="date" v-model="toDate" />
                </div>
              </div>
              <div class="field">
                <label>{{ $t("reports.config.status") }}</label>
                <select class="select" v-model="statusFilter">
                  <option v-for="s in statusOptions" :key="s" :value="s">{{ $t("reports.statusFilter." + s) }}</option>
                </select>
              </div>
            </div>
            <div class="divider"></div>
            <div class="field" style="margin-bottom: 10px">
              <label>{{ $t("reports.config.format") }}</label>
              <div class="row" style="gap: 8px" role="radiogroup" :aria-label="$t('reports.config.format')">
                <button
                  v-for="f in reportFormats"
                  :key="f"
                  class="chip"
                  :class="{ on: format === f }"
                  type="button"
                  role="radio"
                  :aria-checked="format === f ? 'true' : 'false'"
                  :data-test="'format-' + f"
                  @click="format = f"
                >{{ $t("reports.formats." + f) }}</button>
              </div>
            </div>
            <div class="row" style="gap: 10px">
              <button class="btn primary" type="button" :disabled="generating" data-test="generate" @click="generate">
                ⚙️ {{ generating ? $t("reports.actions.generating") : $t("reports.actions.generate") }}
              </button>
            </div>
            <div v-if="job" class="callout mt" data-test="job">
              <div class="row between" style="gap: 10px; flex-wrap: wrap">
                <span class="sm">
                  <span class="muted">{{ $t("reports.job.status") }}:</span>
                  <span class="tag" :class="phaseTag">{{ $t("reports.job.phase." + phase) }}</span>
                  <span class="mono" style="margin-inline-start: 8px">{{ jobMeta.fileName }}</span>
                </span>
                <button
                  v-if="phase === 'done'"
                  class="btn accent sm"
                  type="button"
                  :disabled="downloading"
                  data-test="download"
                  @click="download"
                >⬇️ {{ downloading ? $t("reports.actions.downloading") : $t("reports.actions.download") }}</button>
              </div>
            </div>
            <div v-if="errorMsg" class="note danger" style="margin-top: 10px" role="alert">{{ errorMsg }}</div>
          </div>
        </div>

        <!-- quick summary -->
        <div class="panel">
          <div class="panel__head"><h4>📈 {{ $t("reports.summary.title") }}</h4></div>
          <div class="panel__body">
            <div class="kpis k3" style="gap: 8px">
              <div class="kpi" style="padding: 10px">
                <div class="v mono" style="font-size: 18px">{{ fInt(summary.count) }}</div>
                <div class="l">{{ $t("reports.summary.claims") }}</div>
              </div>
              <div class="kpi" style="padding: 10px">
                <div class="v mono" style="font-size: 18px">{{ fInt(summary.acceptanceRate) }}%</div>
                <div class="l">{{ $t("reports.summary.acceptance") }}</div>
              </div>
              <div class="kpi" style="padding: 10px">
                <div class="v mono" style="font-size: 18px">{{ fInt(summary.totalSpend) }}</div>
                <div class="l">{{ $t("reports.summary.spend") }}</div>
              </div>
            </div>
            <div class="chart mt">
              <div v-for="(bar, i) in bars" :key="i" class="bar">
                <div class="fill" :style="{ height: bar.pct + '%' }" :title="fInt(bar.count)"></div>
                <span class="lb">{{ monthLabel(bar.month) }}</span>
              </div>
            </div>
            <div class="legend">
              <span><i style="background: var(--v-brand)"></i> {{ $t("reports.summary.monthlyLegend") }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- report preview -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>👁️ {{ $t("reports.preview.title") }}</h4>
          <span class="tag muted" style="margin-inline-start: auto">
            {{ fInt(previewRows.length) }} {{ $t("reports.preview.rows") }}
          </span>
        </div>
        <div class="panel__body p0">
          <div v-if="previewRows.length === 0" class="state">{{ $t("reports.preview.empty") }}</div>
          <div v-else class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t("reports.preview.col.no") }}</th>
                  <th>{{ $t("reports.preview.col.employee") }}</th>
                  <th>{{ $t("reports.preview.col.type") }}</th>
                  <th>{{ $t("reports.preview.col.amount") }}</th>
                  <th>{{ $t("reports.preview.col.date") }}</th>
                  <th>{{ $t("reports.preview.col.status") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in previewRows" :key="r.id">
                  <td class="mono">#{{ r.id }}</td>
                  <td>{{ r.employeeName }}</td>
                  <td>{{ r.serviceType }}</td>
                  <td class="mono">{{ fInt(r.amount) }}</td>
                  <td>{{ fDate(r.createdAt) }}</td>
                  <td><span class="tag" :class="tagFor(r.status)">{{ statusLabel(r.status) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost, apiGetBlob } from "../../api/vivo/http";
import {
  REPORT_TYPES,
  REPORT_FORMATS,
  monthlyBars,
  summarize,
  filterRows,
  tagFor,
  buildReportRequest,
  jobPhase,
  downloadMeta,
  filePath,
  pollReport,
  reportErrorKey,
} from "./reports-vm";

export default {
  name: "VivoReports",
  data() {
    return {
      report: null,
      loading: true,
      error: false,
      reportType: "claims",
      reportTypes: REPORT_TYPES,
      statusFilter: "all",
      statusOptions: ["all", "accepted", "rejected", "review"],
      fromDate: "",
      toDate: "",
      q: "",
      reportFormats: REPORT_FORMATS,
      format: "csv",
      job: null,
      generating: false,
      downloading: false,
      errorMsg: "",
    };
  },
  async mounted() {
    await this.loadDashboard();
    this.loading = false;
  },
  computed: {
    phase() {
      return jobPhase(this.job);
    },
    phaseTag() {
      return { done: "ok", failed: "danger", pending: "review", none: "muted" }[this.phase];
    },
    jobMeta() {
      return downloadMeta(this.job);
    },
    activities() {
      // G1: this used to read `recentActivities`, which the HR backend fills
      // from employeeStatusHistory — rows with no employeeName, serviceType,
      // amount or status. Every preview cell was blank, the count was capped at
      // 5, acceptance was always 0% and spend always 0. `recentClaims` is the
      // claim-shaped field (added to match the admin dashboard, which already
      // had it); the fallback keeps an older deployed backend rendering rather
      // than showing an empty table.
      const r = this.report || {};
      return r.recentClaims || r.recentActivities || [];
    },
    previewRows() {
      return filterRows(this.activities, {
        status: this.statusFilter,
        q: this.q,
        from: this.fromDate,
        to: this.toDate,
      });
    },
    summary() {
      return summarize(this.previewRows);
    },
    bars() {
      return this.report ? monthlyBars(this.report.employeesByMonth) : [];
    },
  },
  methods: {
    async loadDashboard() {
      this.error = false;
      try {
        this.report = await apiGet("/reports/dashboard");
      } catch (e) {
        this.error = true;
      }
    },
    tagFor(status) {
      return tagFor(status);
    },
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
    monthLabel(month) {
      return new Intl.DateTimeFormat(this.$i18n.locale, { month: "short", numberingSystem: "latn" }).format(
        new Date(month + "-01T00:00:00Z"),
      );
    },
    statusLabel(status) {
      return this.$t("reports.status." + status);
    },
    async generate() {
      if (this.generating) return;
      this.generating = true;
      this.errorMsg = "";
      this.job = null;
      try {
        const body = buildReportRequest({
          type: this.reportType,
          format: this.format,
          from: this.fromDate,
          to: this.toDate,
          status: this.statusFilter,
        });
        const created = await apiPost("/reports", body);
        this.job = created;
        this.job = await pollReport(
          created,
          (id) => apiGet("/reports/" + encodeURIComponent(id)),
          (ms) => new Promise((r) => setTimeout(r, ms)),
        );
        if (this.phase === "failed") this.errorMsg = this.$t("reports.errors.failed");
      } catch (e) {
        this.errorMsg = this.$t("reports.errors." + reportErrorKey(e));
      } finally {
        this.generating = false;
      }
    },
    async download() {
      if (this.phase !== "done" || this.downloading) return;
      this.downloading = true;
      this.errorMsg = "";
      try {
        const meta = downloadMeta(this.job);
        const data = await apiGetBlob(filePath(this.job));
        // The file endpoint serves octet-stream: re-type the blob from the report.
        const blob = new Blob([data], { type: meta.contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = meta.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        this.errorMsg = this.$t("reports.errors.download");
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>
